export type StandardScoreRule = {
  round_code: string;
  round_name: string;
  round_sequence: number;
  game_win_points: number;
  round_win_points: number;
};

export type StandardScoreLadderRow = {
  roundCode: string;
  roundName: string;
  roundSequence: number;
  gameWinPoints: number;
  roundWinPoints: number;
  winsRequired: number;
  incrementPoints: number;
  cumulativePoints: number;
  isQualification: boolean;
};

export type RoundPointValues = {
  gameWin: number;
  roundBonus: number;
};

export function calculateRoundMaximumPoints(
  roundPoints: RoundPointValues,
  winsRequired: number,
): number {
  return winsRequired * roundPoints.gameWin + roundPoints.roundBonus;
}

type WinsRequiredByRound =
  | Readonly<Record<string, number>>
  | ReadonlyMap<string, number>;

export type BuildStandardScoreLadderOptions = {
  rules: readonly StandardScoreRule[];
  winsRequiredByRound: WinsRequiredByRound;
  qualificationPoints?: number;
  includeQualification: boolean;
  qualificationLabel: string;
};

function getWinsRequired(
  winsRequiredByRound: WinsRequiredByRound,
  roundCode: string,
): number {
  const map = winsRequiredByRound as ReadonlyMap<string, number>;
  if (typeof map.get === 'function') {
    return map.get(roundCode) ?? 1;
  }
  return (winsRequiredByRound as Readonly<Record<string, number>>)[roundCode] ?? 1;
}

export function buildStandardScoreLadder({
  rules,
  winsRequiredByRound,
  qualificationPoints = 0,
  includeQualification,
  qualificationLabel,
}: BuildStandardScoreLadderOptions): StandardScoreLadderRow[] {
  let cumulativePoints = 0;
  const ladder: StandardScoreLadderRow[] = [];

  if (includeQualification && qualificationPoints > 0) {
    cumulativePoints = qualificationPoints;
    ladder.push({
      roundCode: 'MLB_QUALIFICATION',
      roundName: qualificationLabel,
      roundSequence: 0,
      gameWinPoints: 0,
      roundWinPoints: 0,
      winsRequired: 0,
      incrementPoints: qualificationPoints,
      cumulativePoints,
      isQualification: true,
    });
  }

  for (const rule of [...rules].sort(
    (left, right) => left.round_sequence - right.round_sequence,
  )) {
    const winsRequired = getWinsRequired(winsRequiredByRound, rule.round_code);
    const incrementPoints = calculateRoundMaximumPoints(
      {
        gameWin: rule.game_win_points,
        roundBonus: rule.round_win_points,
      },
      winsRequired,
    );
    cumulativePoints += incrementPoints;
    ladder.push({
      roundCode: rule.round_code,
      roundName: rule.round_name,
      roundSequence: rule.round_sequence,
      gameWinPoints: rule.game_win_points,
      roundWinPoints: rule.round_win_points,
      winsRequired,
      incrementPoints,
      cumulativePoints,
      isQualification: false,
    });
  }

  return ladder;
}
